import { useSortable } from "@dnd-kit/sortable";
import { GripVertical, Loader2, Pencil, Trash2 } from "lucide-react";
import { CSS } from "@dnd-kit/utilities";

export default function SortableTokenRow({ contract, companyContracts, onToggle, onEdit, onDelete, isOwned = false, isDraggable = true }) {
  const isDeploying = isOwned && (!contract.address || !contract.abiURL);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: contract.id,
    disabled: !isDraggable,
  });
  const order = companyContracts[contract.id];
  const isEnabled = order !== undefined;

  const style = {
    transform: CSS?.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`transition ${isDragging ? "bg-blue-50" : "hover:bg-gray-50"} ${isEnabled ? "bg-white" : "bg-gray-50/30"}`}
    >
      <td className="py-3.5 px-4">
        <div className="flex items-center gap-2">
          {isDraggable ? (
            <button
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-blue-600"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="w-4 h-4" />
            </button>
          ) : (
            <div className="w-4 h-4 text-gray-300">
              <GripVertical className="w-4 h-4" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800">{contract.name}</span>
            {isEnabled && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                #{order}
              </span>
            )}
            {isDeploying && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                <Loader2 className="w-3 h-3 animate-spin" />
                Deploying
              </span>
            )}
            {contract.fixedPrice !== null && contract.fixedPrice !== undefined && (
              <span
                className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                title="Fixed USD price configured"
              >
                {`Fixed: $${contract.fixedPrice}`}
              </span>
            )}
            {contract.buyable && (
              <span
                className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700"
                title={
                  contract.purchaseFee !== null && contract.purchaseFee !== undefined
                    ? `Buyable (fee: ${(Number(contract.purchaseFee) * 100).toFixed(2)}%)`
                    : "Buyable"
                }
              >
                Buyable
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="py-3.5 px-4">
        {contract.address ? (
          <span className="block max-w-[480px] truncate font-mono text-xs text-gray-600" title={contract.address}>
            {contract.address}
          </span>
        ) : (
          <span className="text-xs text-gray-400 italic">pending deployment…</span>
        )}
      </td>
      <td className="py-3.5 px-4 whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          {isOwned && (
            <>
              <button
                onClick={() => onEdit(contract)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                title="Edit token"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onDelete(contract)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete token"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={() => onToggle(contract.id)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 rounded-full bg-gray-300 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-full" />
          </label>
        </div>
      </td>
    </tr>
  );
}
